/** @format */

import { Grid } from '@material-ui/core';
import { useEffect, useState, useRef } from 'react';
import { flushSync } from 'react-dom';
import { useParams } from 'react-router-dom';
import Breadcrumb from 'src/components/Breadcrumb';
import Page from 'src/components/Page';
import { clearNewstError, getNewsDetail, rateNewsDetail } from 'src/slices/news';
import { useDispatch, useSelector } from 'src/store';
import { ContentBlock } from '../legal-pages/contentBlock';
import { Redirect } from 'react-router';
import { Rating } from 'src/components/Rating';
import { NewsDetailSkeleton } from 'src/components/news/item';
import { useTranslation } from 'react-i18next';

const startViewTransition = callback => {
  if (document.startViewTransition) {
    document.startViewTransition(() => {
      flushSync(callback);
    });
  } else {
    callback();
  }
};

export default function NewsDetail() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { nid } = useParams();
  const { error, globalLoading } = useSelector(state => state.global);
  const {
    newsDetail,
    newsDetailLoading,
    error: newsError,
    ratingError,
    ratingSuccess
  } = useSelector(state => state.news);

  const [displayedContent, setDisplayedContent] = useState({ loading: true, detail: newsDetail });
  const prevLoadingRef = useRef(newsDetailLoading || globalLoading);

  useEffect(() => {
    if (!nid) return;
    dispatch(getNewsDetail(nid, false));
  }, [nid, dispatch]);

  const isLoading = globalLoading || newsDetailLoading;

  useEffect(() => {
    const wasLoading = prevLoadingRef.current;
    prevLoadingRef.current = isLoading;

    if (wasLoading && !isLoading) {
      startViewTransition(() => {
        setDisplayedContent({ loading: false, detail: newsDetail });
      });
    } else if (isLoading && !wasLoading) {
      setDisplayedContent({ loading: true, detail: newsDetail });
    } else if (!isLoading && newsDetail) {
      setDisplayedContent({ loading: displayedContent.loading, detail: newsDetail });
    }
  }, [isLoading, newsDetail]);

  const currentDetailId = displayedContent.detail?.id ?? displayedContent.detail?.nid;
  const hasCurrentDetail = displayedContent.detail && String(currentDetailId) === String(nid);
  const { title: pageTitle, rating, ratingCount, categories } = displayedContent.detail || {};

  if (error || newsError || !nid) return <Redirect to="/404" />;
  return (
    <Page className={`archive-page news-page`} title={pageTitle}>
      <Breadcrumb
        data={[{ title: t('news.title'), link: `/news` }, { title: newsDetail?.title || '' }]}
        additionClassName="container-narrow"
      />
      <Grid className="archive-page-container container container-narrow" style={{ viewTransitionName: 'news-detail' }}>
        {displayedContent.loading && !hasCurrentDetail ? (
          <Grid className="">
            <NewsDetailSkeleton />
          </Grid>
        ) : (
          <div
            style={
              displayedContent.loading
                ? {
                    opacity: 0.2,
                    transition: 'opacity 180ms ease-in-out',
                    pointerEvents: 'none',
                    cursor: 'not-allowed'
                  }
                : undefined
            }
          >
            <Grid>
              <Grid className="archive-page-title-box">
                <h1 className="archive-page-title">{pageTitle}</h1>
              </Grid>
              <Grid className="archive-page-rating">
                <Rating rating={rating} />
                {rating > -1 && (
                  <Grid className="archive-page-rating-count">
                    {rating.toFixed(1)} | {ratingCount} Ratings
                  </Grid>
                )}
              </Grid>
              {categories && (
                <Grid className="archive-page-content-categories">
                  {categories.map(cat => (
                    <Grid key={cat} className="archive-page-content-categories-item">
                      {cat}
                    </Grid>
                  ))}
                </Grid>
              )}
              {displayedContent.detail && (
                <ContentBlock
                  nid={nid}
                  content={displayedContent.detail}
                  rating={rating}
                  clearError={clearNewstError}
                  rateDetail={rateNewsDetail}
                  loading={newsDetailLoading}
                  ratingError={ratingError}
                  ratingSuccess={ratingSuccess}
                  isNews={true}
                />
              )}
            </Grid>
          </div>
        )}
      </Grid>
    </Page>
  );
}
